import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { END_SESSION } from '../../../graphql/mutations';
import { MAX_SESSION_TIME } from '../constants';

export interface UseRoleplaySessionParams {
  sessionId: string;
  /** セッション開始時刻（ISO文字列）。経過時間の起点 */
  sessionStartTime?: string | null;
  /** getServerTime のエポックミリ秒。未取得の間は端末時刻で仮計算する */
  serverTimeMs?: number | null;
  navigate: (path: string) => void;
  /** セッション終了ミューテーション失敗時に呼ばれる。未指定時は従来どおり遷移する（非推奨） */
  onError?: (error: Error) => void;
}

export interface UseRoleplaySessionResult {
  sessionTime: number;
  formatTime: (seconds: number) => string;
  getTimeColor: () => string;
  endSession: () => Promise<void>;
}

export function useRoleplaySession(params: UseRoleplaySessionParams): UseRoleplaySessionResult {
  const { sessionId, sessionStartTime, serverTimeMs, navigate, onError } = params;
  const [sessionTime, setSessionTime] = useState(0);
  const [endSessionMutation] = useMutation(END_SESSION);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = (): string => {
    const remainingTime = MAX_SESSION_TIME - sessionTime;
    if (remainingTime <= 60) return 'error';
    if (remainingTime <= 300) return 'warning';
    return 'primary';
  };

  const endSession = useCallback(async () => {
    try {
      await endSessionMutation({ variables: { sessionId } });
    } catch (mutationError) {
      console.error('Failed to end session with GraphQL:', mutationError);
      const err = mutationError instanceof Error ? mutationError : new Error(String(mutationError));
      if (onError) {
        onError(err);
        return;
      }
      // onError 未指定の場合は従来どおり遷移（後方互換）
    }
    navigate(`/evaluation/${sessionId}`);
  }, [sessionId, navigate, endSessionMutation, onError]);

  const startMsRef = useRef<number | null>(null);
  // サーバー時刻と端末時刻の差。経過時間は Date.now() + offset - 開始時刻
  const offsetRef = useRef(0);
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;
  const endingRef = useRef(false);

  const readElapsed = () => {
    const start = startMsRef.current;
    if (start == null) return 0;
    return Math.max(0, Math.floor((Date.now() + offsetRef.current - start) / 1000));
  };

  const publishElapsed = () => {
    const next = readElapsed();
    setSessionTime(next);
    if (!endingRef.current && next >= MAX_SESSION_TIME) {
      endingRef.current = true;
      void endSessionRef.current();
    }
  };

  useEffect(() => {
    endingRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (sessionStartTime) {
      const ms = new Date(sessionStartTime).getTime();
      startMsRef.current = Number.isNaN(ms) ? null : ms;
    } else {
      startMsRef.current = null;
    }
    if (typeof serverTimeMs === 'number' && Number.isFinite(serverTimeMs)) {
      offsetRef.current = serverTimeMs - Date.now();
    }
    publishElapsed();
  }, [sessionStartTime, serverTimeMs]);

  useEffect(() => {
    const timer = setInterval(publishElapsed, 1000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return {
    sessionTime,
    formatTime,
    getTimeColor,
    endSession,
  };
}
